import { Injectable } from '@angular/core';
import { ResearchApiService } from './research-api.service';
import { AGURenderService } from 'agu_app_shell';
import { ResearchPortalConfig } from '../models/research.models';

/**
 * Memoised `researchPortalConfig` (Career Map URL, account settings URL,
 * jobs-corpus availability, public profile URL) — read once per surface
 * session by the sidebar promo card, Job Finder's availability gate, and
 * Career Map cross-links, rather than every consumer re-querying it.
 *
 * Provided by RaSurfaceComponent (its element injector, not the root) so the
 * memo does not leak between two Research Accelerator tabs open at once —
 * same choice as ResearchApiService and MemberItemStateService.
 */
@Injectable()
export class PortalConfigService {
    private pending: Promise<ResearchPortalConfig> | null = null;

    constructor(
        private readonly api: ResearchApiService,
        private readonly render: AGURenderService
    ) {}

    /** Returns the cached config after the first call; concurrent callers share the same in-flight request. */
    async Load(): Promise<ResearchPortalConfig> {
        if (!this.pending) {
            this.pending = this.api.PortalConfig().catch((error: unknown) => {
                // A failed load must not poison the memo — the next caller gets a fresh attempt.
                this.pending = null;
                throw error;
            });
        }
        try {
            return await this.pending;
        } finally {
            // A memo HIT never reaches ResearchApiService, so it would never
            // repaint the caller on this zoneless host (see AGURenderService).
            this.render.Notify();
        }
    }

    /** Drops the memo so the next `Load` re-reads the config — called when the member returns to the surface (B-3). */
    Clear(): void {
        this.pending = null;
    }
}
